import { motion } from 'framer-motion';
import { Globe, Server, Search, Calendar, Lightbulb, ArrowUpRight, Users, ArrowRight, LucideIcon, TrendingUp, Cpu, Sparkles, GraduationCap, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { IMAGE_WIDTHS, buildSrc, buildSrcSet } from '@/lib/responsiveImage';

interface Service {
  icon: LucideIcon;
  title: string;
  description: string;
  features: string[];
  imageBase: string;
  slug: string;
}

const services: Service[] = [
  {
    icon: Sparkles,
    title: 'KI-Beratung',
    description: 'Strategie, Tool-Auswahl und Integration von KI in Ihre Kernprozesse – mit Claude, Gemini, Copilot und lokalen Open-Source-Modellen.',
    features: ['KI-Strategie', 'Tool-Auswahl', 'Lokale KI'],
    imageBase: '/images/services/ki-beratung',
    slug: 'ki-beratung',
  },
  {
    icon: GraduationCap,
    title: 'KI-Kompetenz-Schulung',
    description: 'Praxis-Schulung für Ihr Team: KI sicher nutzen, Risiken kennen und KI-Kompetenz nach Artikel 4 EU AI Act dokumentieren.',
    features: ['2–5 Stunden', 'Vor Ort oder online', 'Dokumentation inklusive'],
    imageBase: '/images/services/ki-kompetenz-schulung',
    slug: 'ki-kompetenz-schulung',
  },
  {
    icon: Globe,
    title: 'Webdesign',
    description: 'Individuelle Webseiten mit modernem Design, optimaler Usability und blitzschneller Performance für alle Geräte.',
    features: ['Responsive Design', 'CMS-Integration', 'Newsletter-Versand'],
    imageBase: '/images/services/webdesign',
    slug: 'webseitenerstellung',
  },
  {
    icon: ShoppingBag,
    title: 'E-Commerce-Shops',
    description: 'Onlineshops mit WordPress/WooCommerce oder Shopify – inklusive Zahlungen, Versand und rechtssicherer Einrichtung.',
    features: ['WooCommerce & Shopify', 'Zahlungsintegration', 'DHL-Versand'],
    imageBase: '/images/services/onlineshop',
    slug: 'onlineshop-erstellung',
  },

  {
    icon: Search,
    title: 'Search Engine Optimization (SEO)',
    description: 'SEO-Strategien für bessere Rankings, mehr Traffic und langfristige Sichtbarkeit bei Google und anderen Suchmaschinen.',
    features: ['Keyword-Analyse', 'On-Page-Optimierung', 'Local SEO'],
    imageBase: '/images/services/seo',
    slug: 'seo-optimierung',
  },
  {
    icon: TrendingUp,
    title: 'Search Engine Advertising (SEA)',
    description: 'Gezielte Anzeigenkampagnen und Bezahlwerbung bei Google & Co.',
    features: ['Google Ads', 'Displaynetzwerk', 'Conversion Tracking'],
    imageBase: '/images/services/sea',
    slug: 'sea',
  },
  {
    icon: Cpu,
    title: 'Generative Engine Optimization (GEO)',
    description: 'Inhalte KI-freundlich optimieren für die nächste Suchgeneration.',
    features: ['Prompt-optimierung', 'Strukturierte Daten', 'KI/SEO Synergien'],
    imageBase: '/images/services/geo',
    slug: 'geo',
  },
  {
    icon: Users,
    title: 'Karriereseiten',
    description: 'Professionelle Karriereseiten zur Mitarbeitergewinnung und Stärkung Ihrer Arbeitgebermarke.',
    features: ['Stellenausschreibungen', 'Bewerbermanagement', 'Employer Branding'],
    imageBase: '/images/services/karriereseiten',
    slug: 'karriereseiten',
  },
  {
    icon: Calendar,
    title: 'Buchungssysteme',
    description: 'Online-Buchungslösungen für Hotels, Ferienwohnungen, Ärzte und Dienstleister.',
    features: ['Echtzeit-Verfügbarkeit', 'Zahlungsintegration', 'Automatisierung'],
    imageBase: '/images/services/booking',
    slug: 'buchungssysteme',
  },
  {
    icon: Lightbulb,
    title: 'Digitalisierungsberatung',
    description: 'Strategische Beratung für die digitale Transformation – von Prozessoptimierung bis zur Auswahl der richtigen Tools.',
    features: ['Prozessanalyse', 'Tool-Auswahl', 'Schulungen'],
    imageBase: '/images/services/beratung',
    slug: 'digitalisierung-beratung',
  },
  {
    icon: Server,
    title: 'Hosting & Betrieb',
    description: 'Zuverlässiges Hosting und stabiler Betrieb inklusive Monitoring, Updates und Support.',
    features: ['99.9% Uptime', 'Monitoring', 'Migration'],
    imageBase: '/images/services/hosting',
    slug: 'hosting-domains',
  },
];

const Services = () => {
  const imageSizes = '(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw';
  return (
    <section id="leistungen" className="py-24 md:py-32 relative bg-background">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">Unsere Leistungen</span>
          <h2 className="text-3xl md:text-5xl font-display font-bold mt-4 mb-6 text-foreground">
            Welche Leistungen bieten wir für Ihr
            <span className="gradient-text"> Unternehmen?</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Von Webdesign bis zur Digitalisierungsberatung: Wir begleiten Ihr Projekt strategisch von der Idee bis zur Umsetzung.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <Link to={`/leistungen/${service.slug}`} key={service.title}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative rounded-2xl overflow-hidden shadow-lg cursor-pointer h-[380px]"
              >
                {/* Background Image */}
                <img
                  src={buildSrc(service.imageBase, 800)}
                  srcSet={buildSrcSet(service.imageBase, IMAGE_WIDTHS.card)}
                  sizes={imageSizes}
                  alt={`${service.title} – Leistungsbild`}
                  loading="lazy"
                  decoding="async"
                  width={800}
                  height={570}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Dark Overlay */}
                <div className="absolute inset-0 photo-card-overlay" />

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-8">
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-xl bg-primary/20 backdrop-blur-sm flex items-center justify-center mb-4 border border-primary/30">
                    <service.icon className="w-7 h-7 text-primary" aria-hidden="true" />
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-xl font-display font-semibold mb-2 flex items-center gap-2 text-photo-foreground">
                    {service.title}
                    <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity text-primary" aria-hidden="true" />
                  </h3>
                  <p className="text-photo-foreground mb-4 text-sm leading-relaxed">{service.description}</p>

                  {/* Features */}
                  <ul className="flex flex-wrap gap-2">
                    {service.features.map((feature) => (
                      <li 
                        key={feature} 
                        className="text-xs px-3 py-1 rounded-full bg-photo-badge text-photo-foreground border border-photo-border"
                      >
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
        <Link
          to="/leistungen"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-lg"
        >
          Alle Leistungen sehen
          <ArrowRight className="w-5 h-5" aria-hidden="true" />
        </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
